// LinkedIn Insight Tag, and the purchase conversion behind it.
//
// The partner id is not a secret — it ships in the HTML of every page that
// carries the tag — so it lives here rather than in an env var, and the tag is
// live as soon as this deploys. REACT_APP_LINKEDIN_PARTNER_ID still overrides
// it, and setting that variable to an EMPTY string switches the tag off
// entirely without a code change. Hence ?? rather than ||: an empty string is
// a deliberate "off", not a missing value to fall back from.
//
// The purchase conversion is fired from the SERVER-CONFIRMED paid state, not
// from arriving at /research/complete. Somebody who abandons Stripe and then
// types that URL, or reloads the page, must not be counted as a sale.
//
// LIMITATION WORTH KNOWING: the browser tag posts the fact of a conversion,
// not its value — lintrk() has no amount parameter, so LinkedIn records the
// conversion's default value. With editions from $295 to $3,995 that default
// cannot be right for all of them, so conversion COUNTS are trustworthy here
// and revenue is not. Real revenue needs the server-side Conversions API,
// called from the Stripe webhook where the true amount and the buyer's email
// are both known.

const PARTNER_ID = process.env.REACT_APP_LINKEDIN_PARTNER_ID ?? '9645882';
const PURCHASE_CONVERSION_ID = process.env.REACT_APP_LINKEDIN_PURCHASE_CONVERSION_ID || '29893010';

export const linkedInEnabled = () => Boolean(PARTNER_ID);

let injected = false;

/** Inject the base tag once. Safe to call repeatedly. */
export function initLinkedInInsight() {
  if (injected || !PARTNER_ID || typeof window === 'undefined') return;
  injected = true;

  window._linkedin_partner_id = String(PARTNER_ID);
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(String(PARTNER_ID));

  // lintrk queues calls made before the script finishes loading, so a
  // conversion fired early is not lost.
  if (!window.lintrk) {
    window.lintrk = function (a, b) { window.lintrk.q.push([a, b]); };
    window.lintrk.q = [];
  }

  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = true;
  s.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
  document.head.appendChild(s);
}

/**
 * Record one purchase. Returns true if it was sent.
 * Callers must guard against firing twice for the same order.
 */
export function trackLinkedInPurchase() {
  if (!PARTNER_ID || typeof window === 'undefined' || !window.lintrk) return false;
  try {
    window.lintrk('track', { conversion_id: Number(PURCHASE_CONVERSION_ID) });
    return true;
  } catch {
    return false;
  }
}
